import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, TrendingUp, Users, UtensilsCrossed, CreditCard, RefreshCw, Clock, CalendarDays } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../api/axios';

const PIE_COLORS = ['#8B0000', '#B49632', '#2c6e49', '#32415A', '#784632', '#C8501E', '#501464', '#3C463C'];
const METHOD_LABELS = { 'CB': 'Carte Bancaire', 'Espece': 'Especes', 'Virement': 'Virement', 'Cheque': 'Cheque', 'Cheque Restaurant': 'Cheque Restaurant' };

const PERIODS = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'week', label: 'Semaine' },
  { id: 'month', label: 'Mois' },
  { id: 'all', label: 'Tout' },
];

const StaffStats = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get(`/dashboard/stats?period=${period}`);
      setStats(res.data);
      setLastRefresh(new Date());
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de charger les statistiques', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    const role = localStorage.getItem('staffRole');
    if (!token) { navigate('/staff/login'); return; }
    if (!['webmaster', 'admin'].includes(role)) { navigate('/staff/dashboard'); return; }
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [navigate, fetchStats]);

  const handlePeriod = (p) => {
    setPeriod(p);
    setLoading(true);
  };

  if (loading || !stats) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a0f0a] to-[#2c1810] flex items-center justify-center">
        <RefreshCw className="animate-spin text-[#8B0000]" size={32} />
      </div>
    );
  }

  const pieData = Object.entries(stats.by_method).map(([method, amount]) => ({
    name: METHOD_LABELS[method] || method,
    value: amount,
    count: stats.method_count?.[method] || 0,
  }));

  const useDaily = stats.daily_revenue && stats.daily_revenue.length > 1;
  const barData = useDaily
    ? stats.daily_revenue.map(d => ({ name: d.day, revenue: d.revenue }))
    : stats.hourly_revenue.map(h => ({ name: `${h.hour}h`, revenue: h.revenue }));
  const barLabel = useDaily ? 'CA par jour' : 'CA par heure';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0f0a] via-[#2c1810] to-[#1a0f0a]">
      {/* Header */}
      <div className="bg-[#0f0806]/80 backdrop-blur border-b border-[#8B0000]/20 py-4">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-3">
            <div className="bg-[#8B0000] p-2 rounded-lg">
              <TrendingUp size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white" data-testid="stats-title">Tableau de Bord</h1>
              <p className="text-xs text-gray-400">{stats.period_label}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {lastRefresh && (
              <span className="text-[10px] text-gray-500 flex items-center gap-1">
                <Clock size={10} /> {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Cayenne' })}
              </span>
            )}
            <Button onClick={fetchStats} variant="outline" size="sm" className="text-xs border-gray-600 text-gray-300 hover:bg-gray-800" data-testid="refresh-btn">
              <RefreshCw size={12} className="mr-1" /> Actualiser
            </Button>
            <Button onClick={() => navigate('/staff/dashboard')} size="sm" className="bg-[#8B0000] hover:bg-[#6d0000] text-xs" data-testid="back-dashboard-btn">
              <ArrowLeft size={14} className="mr-1" /> Dashboard
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
        {/* Period Filter */}
        <div className="flex items-center gap-2" data-testid="period-filter">
          <CalendarDays size={14} className="text-gray-400" />
          <span className="text-xs text-gray-400 mr-1">Periode :</span>
          {PERIODS.map(p => (
            <Button
              key={p.id}
              onClick={() => handlePeriod(p.id)}
              size="sm"
              className={`text-xs h-7 px-3 ${period === p.id
                ? 'bg-[#8B0000] text-white hover:bg-[#6d0000]'
                : 'bg-[#2c1810] text-gray-400 hover:bg-[#3d2817] hover:text-white border border-[#8B0000]/20'
              }`}
              data-testid={`period-${p.id}`}
            >
              {p.label}
            </Button>
          ))}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
          <Card className="bg-gradient-to-br from-[#8B0000] to-[#5c0000] border-0 shadow-lg shadow-red-900/20">
            <CardContent className="p-4">
              <p className="text-xs text-red-200/70 uppercase tracking-wider font-medium">Chiffre d'affaires</p>
              <p className="text-3xl font-black text-white mt-1" data-testid="kpi-revenue">{stats.total_revenue.toFixed(2)}<span className="text-lg ml-1">EUR</span></p>
              <p className="text-[10px] text-red-200/50 mt-1">{stats.transaction_count} transaction{stats.transaction_count > 1 ? 's' : ''}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#2c6e49] to-[#1a4a30] border-0 shadow-lg shadow-green-900/20">
            <CardContent className="p-4">
              <p className="text-xs text-green-200/70 uppercase tracking-wider font-medium">Ticket moyen</p>
              <p className="text-3xl font-black text-white mt-1" data-testid="kpi-avg">{stats.avg_ticket.toFixed(2)}<span className="text-lg ml-1">EUR</span></p>
              <p className="text-[10px] text-green-200/50 mt-1">par transaction</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#32415A] to-[#1e2a3d] border-0 shadow-lg shadow-blue-900/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-blue-200/70" />
                <p className="text-xs text-blue-200/70 uppercase tracking-wider font-medium">Couverts</p>
              </div>
              <p className="text-3xl font-black text-white mt-1" data-testid="kpi-covers">{stats.covers}</p>
              <p className="text-[10px] text-blue-200/50 mt-1">{stats.tables_served} table{stats.tables_served > 1 ? 's' : ''} servie{stats.tables_served > 1 ? 's' : ''}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#784632] to-[#4a2a1e] border-0 shadow-lg shadow-amber-900/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <UtensilsCrossed size={16} className="text-amber-200/70" />
                <p className="text-xs text-amber-200/70 uppercase tracking-wider font-medium">Plats vendus</p>
              </div>
              <p className="text-3xl font-black text-white mt-1" data-testid="kpi-dishes">
                {stats.popular_dishes.reduce((s, d) => s + d.count, 0)}
              </p>
              <p className="text-[10px] text-amber-200/50 mt-1">{stats.popular_dishes.length} reference{stats.popular_dishes.length > 1 ? 's' : ''}</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Bar Chart */}
          <Card className="bg-[#1e1410]/80 backdrop-blur border-[#8B0000]/15 shadow-lg">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-bold text-gray-200 flex items-center gap-2">
                <Clock size={14} className="text-[#8B0000]" /> {barLabel}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-4">
              {barData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="name" tick={{ fill: '#888', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#888', fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a0f0a', border: '1px solid #8B0000', borderRadius: 8, color: '#fff', fontSize: 12 }}
                      formatter={(value) => [`${value.toFixed(2)} EUR`, 'CA']}
                    />
                    <Bar dataKey="revenue" fill="#8B0000" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-gray-500 py-12 text-sm">Aucune donnee</p>
              )}
            </CardContent>
          </Card>

          {/* Pie Chart */}
          <Card className="bg-[#1e1410]/80 backdrop-blur border-[#8B0000]/15 shadow-lg">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-bold text-gray-200 flex items-center gap-2">
                <CreditCard size={14} className="text-[#8B0000]" /> Modes de paiement
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-4">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a0f0a', border: '1px solid #8B0000', borderRadius: 8, color: '#fff', fontSize: 12 }}
                      formatter={(value, name, props) => [`${value.toFixed(2)} EUR (${props.payload.count} tx)`, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-gray-500 py-12 text-sm">Aucune donnee</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Top Dishes */}
        <Card className="bg-[#1e1410]/80 backdrop-blur border-[#8B0000]/15 shadow-lg">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-bold text-gray-200 flex items-center gap-2">
              <UtensilsCrossed size={14} className="text-[#8B0000]" /> Top 10 - Plats les plus vendus
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {stats.popular_dishes.length > 0 ? (
              <div className="space-y-2">
                {stats.popular_dishes.map((dish, i) => {
                  const maxCount = stats.popular_dishes[0]?.count || 1;
                  const pct = (dish.count / maxCount) * 100;
                  return (
                    <div key={i} data-testid={`dish-rank-${i}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-[#8B0000] w-6 text-right">#{i + 1}</span>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm text-gray-200 font-medium">{dish.name}</span>
                            <div className="text-right">
                              <span className="text-xs font-bold text-white">{dish.count} vendus</span>
                              <span className="text-[10px] text-gray-500 ml-2">{dish.revenue.toFixed(2)} EUR</span>
                            </div>
                          </div>
                          <div className="h-1.5 bg-[#2c1810] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-[#8B0000] to-[#B49632] rounded-full transition-all duration-700"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8 text-sm">Aucune commande</p>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-[10px] text-gray-600">Actualisation automatique toutes les 30 secondes</p>
      </div>
    </div>
  );
};

export default StaffStats;
